import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Lock, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Input } from '@/components/ui/input'
import { useToast } from '@/hooks/use-toast'
import { handleApiError } from '@/lib/api/axios'
import { classificationsApi, type Classification, type ClassificationRuleUsage } from '../api'

const isActive = (c: Classification) => Number(c.Active ?? 1) === 1
const isShipped = (c: Classification) => Number(c.IsSystem ?? 0) === 1
const rankOf = (c: Classification) => Number(c.DisplayRank)
const usageKey = (dependent: number, provider: number) => `${dependent}|${provider}`

/**
 * One classification's dependency rules: what it may depend on, and what may depend on it.
 *
 * Andrew, 2026-09-22: "Build a rules editor on the classifications screen". The rules were data
 * loaded by SQL scripts, with no screen to read or change them, and he doubted some of them.
 *
 * A dependency is drawn DOWN the bands: the planning canvas only lets a provider sit below its
 * dependent (connectionValidity.rankAllows). So the classification may depend on classifications
 * ranked below it (the left list) and be depended on by those ranked above it (the right list),
 * grouped by category in band order. A rule already stored against that order is listed apart and
 * flagged: the planning canvas can never draw it. It can be kept or removed here, but no new one
 * can be added.
 *
 * A rule cannot be removed while anything uses it, a dependency on the canvas or in a template.
 * Those would be left with nothing to allow them. The count is shown against the rule, and the
 * server (PUT /api/classifications/:id/rules) refuses too. Between two shipped classifications a
 * rule is VaultSentinel's, locked where shipped content is not authored, as everywhere else. A
 * customer's own classification may be connected to anything.
 *
 * Saved whole in one request: the server works out the difference and writes it in one
 * transaction. Rules to retired classifications are not shown, and a save leaves them alone.
 *
 * Read-only for a role without classifications.update: the rules are shown, nothing can be ticked,
 * and there is no save.
 */
export function ClassificationRulesDialog({ classification, classifications, authoring, readOnly = false, open, onOpenChange }: {
    classification: Classification | null
    classifications: Classification[]
    authoring: boolean
    readOnly?: boolean
    open: boolean
    onOpenChange: (open: boolean) => void
}) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [search, setSearch] = useState('')

    // No `= []` defaults: the seeding effect below depends on these, and a fresh array on every
    // render would re-run it on every render until the answers came.
    const { data: rules } = useQuery({
        queryKey: ['classification-rules'],
        queryFn: async () => (await classificationsApi.rules()).data.data,
        enabled: open,
    })
    const { data: usage } = useQuery({
        queryKey: ['classification-rule-usage'],
        queryFn: async () => (await classificationsApi.ruleUsage()).data.data,
        enabled: open,
    })

    const self = classification
    const others = useMemo(
        () => (self ? classifications.filter((c) => isActive(c) && c.id !== self.id) : []),
        [classifications, self],
    )
    const byId = useMemo(() => new Map(others.map((c) => [c.id, c])), [others])

    // What is stored, within what this dialog shows: rules with the other active classifications.
    const stored = useMemo(() => {
        if (!self || !rules) return null
        const providers = new Set<number>()
        const dependents = new Set<number>()
        for (const r of rules) {
            if (r.DependentClassID === self.id && byId.has(r.ProviderClassID)) providers.add(r.ProviderClassID)
            if (r.ProviderClassID === self.id && byId.has(r.DependentClassID)) dependents.add(r.DependentClassID)
        }
        return { providers, dependents }
    }, [self, rules, byId])

    const [providers, setProviders] = useState<Set<number>>(new Set())
    const [dependents, setDependents] = useState<Set<number>>(new Set())
    // Seeded from the server once each time the dialog opens, so a cancelled edit is not what
    // reopens, and a refetch while it is open (the list refreshes on window focus) cannot wipe
    // the ticks being made.
    const [seeded, setSeeded] = useState(false)
    useEffect(() => {
        if (!open) setSeeded(false)
    }, [open])
    useEffect(() => {
        if (!open || !stored || seeded) return
        setProviders(new Set(stored.providers))
        setDependents(new Set(stored.dependents))
        setSearch('')
        setSeeded(true)
    }, [open, stored, seeded])

    const usageOf = useMemo(() => {
        const map = new Map<string, ClassificationRuleUsage>()
        for (const u of usage ?? []) map.set(usageKey(u.DependentClassID, u.ProviderClassID), u)
        return map
    }, [usage])

    const save = useMutation({
        mutationFn: async () => {
            if (!self) return
            await classificationsApi.setRules(self.id, { DependsOn: [...providers], DependedOnBy: [...dependents] })
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['classification-rules'] })
            queryClient.invalidateQueries({ queryKey: ['classification-rule-usage'] })
            toast({ title: t('classifications.rulesSaved', 'Dependency rules saved') })
            onOpenChange(false)
        },
        onError: (e) => {
            toast({ title: t('common.error', 'Error'), description: handleApiError(e), variant: 'destructive' })
        },
    })

    if (!open || !self) return null

    const selfRank = rankOf(self)
    const term = search.trim().toLowerCase()
    const matches = (c: Classification) => !term
        || c.Name.toLowerCase().includes(term)
        || String(c.Category || '').toLowerCase().includes(term)
    const locked = (other: Classification) => isShipped(self) && isShipped(other) && !authoring

    // The two lists, by band: below may be depended on, above may depend.
    const below = others.filter((c) => rankOf(c) < selfRank)
    const above = others.filter((c) => rankOf(c) > selfRank)
    // Stored rules the planning canvas can never draw: a provider at or above, a dependent at or below.
    const againstProviders = stored ? [...stored.providers].map((id) => byId.get(id)!).filter((c) => rankOf(c) >= selfRank) : []
    const againstDependents = stored ? [...stored.dependents].map((id) => byId.get(id)!).filter((c) => rankOf(c) <= selfRank) : []

    const changed = stored
        ? {
            added: [...providers].filter((id) => !stored.providers.has(id)).length
                + [...dependents].filter((id) => !stored.dependents.has(id)).length,
            removed: [...stored.providers].filter((id) => !providers.has(id)).length
                + [...stored.dependents].filter((id) => !dependents.has(id)).length,
        }
        : { added: 0, removed: 0 }

    const toggle = (set: Set<number>, update: (s: Set<number>) => void, id: number, on: boolean) => {
        const next = new Set(set)
        if (on) next.add(id)
        else next.delete(id)
        update(next)
    }

    /**
     * One classification as a candidate, ticked when the rule exists (or will, once saved). Named
     * by the other classification in the two lists, whose headings say the direction; `label`
     * spells the rule out where the list mixes both directions.
     */
    const row = (other: Classification, checked: boolean, onChange: (on: boolean) => void, dependent: number, provider: number, label = other.Name) => {
        const u = usageOf.get(usageKey(dependent, provider))
        const inUse = (u?.MappingCount ?? 0) + (u?.TemplateDependencyCount ?? 0)
        const isLocked = locked(other)
        // A rule in use cannot go; one nothing uses can. Unticked with uses is a dependency that
        // exists with no rule allowing it: ticking it is how that is put right.
        const disabled = readOnly || isLocked || (checked && inUse > 0)
        const title = isLocked
            ? t('classifications.rulesLocked', 'Maintained by VaultSentinel')
            : inUse > 0
                ? t('classifications.rulesInUseTitle', '{{mappings}} dependencies on the canvas and {{templates}} in templates use this rule.', {
                    mappings: u?.MappingCount ?? 0, templates: u?.TemplateDependencyCount ?? 0,
                })
                : undefined
        return (
            <label
                key={`${dependent}-${provider}`}
                className={`flex items-center gap-2 rounded px-2 py-1 text-xs ${disabled ? 'opacity-70' : 'cursor-pointer hover:bg-muted/50'}`}
                title={title}
            >
                <Checkbox
                    checked={checked}
                    disabled={disabled}
                    onCheckedChange={(v) => onChange(v === true)}
                    aria-label={label}
                />
                <span className="min-w-0 truncate">{label}</span>
                <span className="ml-auto flex shrink-0 items-center gap-1.5">
                    {inUse > 0 && (
                        <span className="rounded bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
                            {t('classifications.rulesInUse', '{{count}} in use', { count: inUse })}
                        </span>
                    )}
                    {isLocked && <Lock className="h-3 w-3 text-muted-foreground" aria-hidden />}
                    <span className="w-9 text-right text-[10px] tabular-nums text-muted-foreground">{other.DisplayRank}</span>
                </span>
            </label>
        )
    }

    /** A list grouped by category, the groups and their rows in band order, top first. */
    const grouped = (list: Classification[], render: (c: Classification) => ReactNode) => {
        const shown = list.filter(matches).sort((a, b) => rankOf(b) - rankOf(a) || a.Name.localeCompare(b.Name))
        const groups = new Map<string, Classification[]>()
        for (const c of shown) {
            const key = c.Category || t('classifications.uncategorised', 'Uncategorised')
            if (!groups.has(key)) groups.set(key, [])
            groups.get(key)!.push(c)
        }
        if (!shown.length) {
            return (
                <p className="px-2 py-3 text-xs text-muted-foreground">
                    {term ? t('classifications.rulesNoMatch', 'No classification matches that.') : t('classifications.rulesNone', 'None.')}
                </p>
            )
        }
        return [...groups.entries()].map(([category, members]) => (
            <div key={category} className="mb-1.5">
                <div className="px-2 pb-0.5 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{category}</div>
                {members.map(render)}
            </div>
        ))
    }

    const column = (heading: string, count: number, body: ReactNode) => (
        <section className="flex min-h-0 flex-col rounded-md border" aria-label={heading}>
            <h3 className="flex items-center justify-between border-b px-3 py-2 text-xs font-semibold">
                {heading}
                <span className="font-normal tabular-nums text-muted-foreground">{count}</span>
            </h3>
            <div className="max-h-[46vh] min-h-[8rem] overflow-y-auto p-1">{body}</div>
        </section>
    )

    return (
        <>
            <div className="fixed inset-0 z-[199] bg-black/20" onClick={() => onOpenChange(false)} aria-hidden />
            <div
                role="dialog"
                aria-modal="true"
                aria-label={t('classifications.rulesTitle', 'Dependency rules for {{name}}', { name: self.Name })}
                className="fixed left-1/2 top-1/2 z-[200] flex max-h-[90vh] w-[860px] max-w-[95vw] -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-lg border bg-background px-5 py-4 shadow-xl"
            >
                <div>
                    <h2 className="text-sm font-semibold">
                        {t('classifications.rulesTitle', 'Dependency rules for {{name}}', { name: self.Name })}
                    </h2>
                    <p className="mt-1 text-xs text-muted-foreground">
                        {t('classifications.rulesHelp',
                            '{{name}} is in band {{rank}}. A dependency is drawn down the bands, so it may depend on classifications below it, and those above it may depend on it. The canvases allow only the dependencies ticked here.',
                            { name: self.Name, rank: self.DisplayRank })}
                    </p>
                </div>

                <div className="relative">
                    <Search className="absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                    <Input
                        className="h-8 pl-7 text-xs"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder={t('classifications.rulesSearch', 'Filter classifications…')}
                        aria-label={t('classifications.rulesSearch', 'Filter classifications…')}
                    />
                </div>

                {!stored || !seeded ? (
                    <p className="py-6 text-center text-xs text-muted-foreground">{t('common.loading', 'Loading...')}</p>
                ) : (
                    <div className="grid min-h-0 grid-cols-1 gap-3 md:grid-cols-2">
                        {column(
                            t('classifications.rulesDependsOn', '{{name}} may depend on', { name: self.Name }),
                            below.filter((c) => providers.has(c.id)).length,
                            grouped(below, (c) => row(c, providers.has(c.id), (on) => toggle(providers, setProviders, c.id, on), self.id, c.id)),
                        )}
                        {column(
                            t('classifications.rulesDependedOnBy', 'May depend on {{name}}', { name: self.Name }),
                            above.filter((c) => dependents.has(c.id)).length,
                            grouped(above, (c) => row(c, dependents.has(c.id), (on) => toggle(dependents, setDependents, c.id, on), c.id, self.id)),
                        )}
                    </div>
                )}

                {(againstProviders.length > 0 || againstDependents.length > 0) && (
                    <section
                        className="rounded-md border border-amber-500/40 bg-amber-500/10 p-2"
                        aria-label={t('classifications.rulesAgainstBands', 'Against the band order')}
                    >
                        <div className="flex items-start gap-2 px-1 pb-1 text-xs">
                            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0 text-amber-600 dark:text-amber-400" aria-hidden />
                            <div>
                                <div className="font-semibold">{t('classifications.rulesAgainstBands', 'Against the band order')}</div>
                                <div className="text-muted-foreground">
                                    {t('classifications.rulesAgainstBandsHelp',
                                        'The planning canvas can never draw these: the classification relied on is in the same band or a higher one. Untick to remove.')}
                                </div>
                            </div>
                        </div>
                        {againstProviders.map((c) => row(c, providers.has(c.id), (on) => toggle(providers, setProviders, c.id, on), self.id, c.id,
                            t('classifications.rulesPair', '{{dependent}} may depend on {{provider}}', { dependent: self.Name, provider: c.Name })))}
                        {againstDependents.map((c) => row(c, dependents.has(c.id), (on) => toggle(dependents, setDependents, c.id, on), c.id, self.id,
                            t('classifications.rulesPair', '{{dependent}} may depend on {{provider}}', { dependent: c.Name, provider: self.Name })))}
                    </section>
                )}

                <div className="flex items-center justify-between gap-2 border-t pt-3">
                    <span className="text-xs text-muted-foreground" aria-live="polite">
                        {changed.added || changed.removed
                            ? t('classifications.rulesSummary', '{{added}} to add, {{removed}} to remove', changed)
                            : t('classifications.rulesNoChanges', 'No changes')}
                    </span>
                    <div className="flex gap-2">
                        {readOnly ? (
                            <Button variant="outline" onClick={() => onOpenChange(false)}>
                                {t('common.close', 'Close')}
                            </Button>
                        ) : (<>
                            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={save.isPending}>
                                {t('common.cancel', 'Cancel')}
                            </Button>
                            <Button onClick={() => save.mutate()} disabled={save.isPending || !stored || (!changed.added && !changed.removed)}>
                                {save.isPending ? t('common.saving', 'Saving...') : t('common.save', 'Save')}
                            </Button>
                        </>)}
                    </div>
                </div>
            </div>
        </>
    )
}
